import styled, {keyframes} from "styled-components";

type ContainerType = {
  background: string;
  border: string;
};

type TextType = {
  color: string;
};

type PillType = {
  color: string;
  background: string;
};

type SkeletonBoneType = {
  background: string;
  highlight: string;
  width?: string;
  height?: string;
  borderRadius?: string;
};

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const TableCard = styled.div<ContainerType>`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.background};
  border: 1px solid ${(props) => props.border};
  border-radius: 10px;
  overflow: hidden;
  min-width: 0;
  height: 100%;
`;

export const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 16px 16px 12px;
`;

export const TableTitle = styled.span<TextType>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.color};
  line-height: 1.3;
`;

export const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const TableHead = styled.div<{ border: string }>`
  display: grid;
  grid-template-columns: 36px minmax(0, 1.6fr) 64px 64px 88px;
  gap: 8px;
  padding: 8px 16px;
  border-top: 1px solid ${(props) => props.border};
  border-bottom: 1px solid ${(props) => props.border};
`;

export const HeadCell = styled.span<TextType>`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.color};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const TableRow = styled.div<{ border: string }>`
  display: grid;
  grid-template-columns: 36px minmax(0, 1.6fr) 64px 64px 88px;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;

  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.border};
  }
`;

export const RankCell = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.color};
`;

export const NameCell = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const IconCircle = styled.div<{ background: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: ${(props) => props.background};
`;

export const NameText = styled.span<TextType>`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ValueCell = styled.span<TextType>`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.color};
  text-align: right;
`;

export const VariationCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const VariationPill = styled.div<PillType>`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

export const EmptyState = styled.div<TextType>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  font-size: 13px;
  color: ${(props) => props.color};
`;

export const HighlightRow = styled.div<{ border: string; background: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.border};
  background-color: ${(props) => props.background};
`;

export const HighlightText = styled.span<TextType>`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.color};
  line-height: 1.4;
`;

export const SkeletonBone = styled.div<SkeletonBoneType>`
  width: ${(props) => props.width ?? '100%'};
  height: ${(props) => props.height ?? '14px'};
  border-radius: ${(props) => props.borderRadius ?? '6px'};
  background: linear-gradient(
    90deg,
    ${(props) => props.background} 25%,
    ${(props) => props.highlight} 50%,
    ${(props) => props.background} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;
